import {
  Button,
  Card,
  Divider,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  Link,
  useDisclosure,
} from '@chakra-ui/react';
import React from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { Link as RouterLink } from 'react-router-dom';

import { UploadedDocumentTypeGQLType } from '../../../../../types/dashboard-core-gqlTypes.generated.d';
import { CreditFacilityDocumentsHistoryTable } from './CreditFacilityDocumentsHistoryTable';
import { UPLOADED_DOCUMENT_TYPE_TO_LABEL } from './documentTypes';
import { useDocumentUploadDirectUpload } from './useDirectUpload';
import { useUploadCreditAgreementToKnowledgeBaseMutation } from 'packages/credit-facility/requests/requests.gql.generated';
import { useFeatureFlags } from '~/contexts/FeatureFlagProvider';
import { IconButtonV2 } from '~/packages/common-frontend/components/IconButton';
import { Icon } from '~/packages/common-frontend/components/icons';
import { SelectInput } from '~/packages/common-frontend/components/Inputs';
import { UploadModal } from '~/packages/common-frontend/components/ReactFlow/UploadModal';
import { Typography } from '~/packages/common-frontend/components/Typography';

type UploadedDocumentFormData = {
  documentType: UploadedDocumentTypeGQLType;
  capitalProviderId: string | null;
  uploadedAt: Date;
};
type RequiredLenderNameFormData = Omit<
  UploadedDocumentFormData,
  'capitalProviderId'
> & {
  capitalProviderId: string;
};

const DOCUMENT_TYPE_OPTIONS = Object.entries(
  UPLOADED_DOCUMENT_TYPE_TO_LABEL,
).map(([docType, label]) => ({
  label,
  value: docType,
}));

export const CreditFacilityUploadDocumentForm = ({
  capitalProviderOptions,
  creditFacilityId,
}: {
  capitalProviderOptions: { label: string; value: string }[];
  creditFacilityId: string;
}) => {
  const form = useForm<UploadedDocumentFormData>();
  const uploadedDocumentType = useWatch({
    control: form.control,
    name: 'documentType',
  });
  const capitalProviderId = useWatch({
    control: form.control,
    name: 'capitalProviderId',
  });
  const modalControls = useDisclosure();

  const { directUpload } = useDocumentUploadDirectUpload();

  const [
    uploadCaToKnowledgeBase,
  ] = useUploadCreditAgreementToKnowledgeBaseMutation();

  return (
    <Card gap="1.5rem" p="2rem" variant="outline" w="100%">
      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(() => modalControls.onOpen())}>
          <SelectInput<UploadedDocumentFormData>
            label="File Type"
            name="documentType"
            placeholder="Select"
            options={DOCUMENT_TYPE_OPTIONS}
            required
          />
          {uploadedDocumentType === 'participationAgreement' && (
            <SelectInput<RequiredLenderNameFormData>
              helperText={
                <>
                  Configure lenders in{' '}
                  <Link as={RouterLink} to="./parties">
                    <Typography
                      inline
                      colorScheme="blue"
                      fontWeight="medium"
                      variant="detail-muted"
                    >
                      parties.
                    </Typography>
                  </Link>
                </>
              }
              label="Lender"
              name="capitalProviderId"
              placeholder="Select"
              options={capitalProviderOptions}
              required
            />
          )}
          <Flex justifyContent="end">
            <Button colorScheme="v2-aurora" type="submit">
              Upload
            </Button>
          </Flex>
          <UploadModal
            isOpen={modalControls.isOpen}
            modalHeaderText="Document Storage"
            onClose={modalControls.onClose}
            handleFile={async (file) => {
              const { creditFacilityDocumentId } = await directUpload({
                file,
                creditFacilityId,
                capitalProviderId,
                uploadedDocumentType,
              });
              if (uploadedDocumentType === 'creditAgreement') {
                await uploadCaToKnowledgeBase({
                  variables: { creditFacilityDocumentId },
                });
              }
            }}
            allowedExtensions={['jpeg', 'jpg', 'pdf', 'png', 'txt']}
          />
        </form>
      </FormProvider>
    </Card>
  );
};

export const CreditFacilityDocumentUploadDrawer = ({
  capitalProviderOptions,
  creditFacilityId,
}: {
  capitalProviderOptions: { label: string; value: string }[];
  creditFacilityId: string;
}) => {
  const drawerControls = useDisclosure();
  const flags = useFeatureFlags();

  return (
    <>
      <IconButtonV2
        aria-label="Upload"
        icon={<Icon icon="file-arrow-up" />}
        onClick={drawerControls.onOpen}
        variant="primary-muted"
        isDisabled={
          flags.productsImplementationPortalDocumentUploadPermissions !==
          'allow'
        }
      />
      <Drawer
        onClose={drawerControls.onClose}
        isOpen={drawerControls.isOpen}
        size="xl"
      >
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader textAlign="left" mt="1.5rem" px="3rem" pb="0">
            <Typography variant="section-title">Document Storage</Typography>
          </DrawerHeader>
          <Divider my="1.5rem" />
          <DrawerBody>
            <Flex direction="column" gap="2rem">
              <Typography variant="detail-muted">
                Upload and store your credit agreement and participation
                agreements.
              </Typography>
              <CreditFacilityUploadDocumentForm
                capitalProviderOptions={capitalProviderOptions}
                creditFacilityId={creditFacilityId}
              />
              <CreditFacilityDocumentsHistoryTable
                creditFacilityId={creditFacilityId}
              />
            </Flex>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </>
  );
};
